/**
 * Careers registry — one entry per open telemedicine specialty panel.
 *
 * Drives:
 *  - /careers hub grid
 *  - /careers/[slug] specialty landing pages
 *  - JobPosting JSON-LD (Google Jobs eligibility)
 *  - Sitemap entries (via seo-config.ts)
 *
 * Positioning: premium telemedicine partner — NPI-verified, minimum 3 years
 * post-residency (or post-credential for allied roles).
 */

export type CareerCategory = "physician" | "allied";

export interface CareerSpec {
  /** URL slug — becomes /careers/[slug] */
  slug: string;
  /** Human display name — "Endocrinology", "Case Management (RN)" */
  displayName: string;
  /** SEO keyword form — "Telemedicine Endocrinology" */
  seoName: string;
  /** SPECIALTIES dropdown value for SpecialistApplicationForm prefill */
  applicationSpecialty: string;
  /** Short specialty title used in body copy — "endocrinologist" */
  clinicianTitle: string;
  category: CareerCategory;
  /** ~30-word blurb for the /careers hub card */
  blurb: string;
  /** Bullet list of what the panel actually does day-to-day (4-6 items) */
  panelWork: string[];
  /** Bullet list of requirements beyond the site-wide minimums (0-4 items) */
  extraRequirements?: string[];
  /** ISO date used for JobPosting datePosted (keep evergreen-ish) */
  datePosted?: string;
}

// Evergreen: refreshed each build cycle. Update yearly.
const DEFAULT_POSTED = "2026-01-01";

export const CAREER_SPECS: CareerSpec[] = [
  {
    slug: "endocrinology",
    displayName: "Endocrinology",
    seoName: "Telemedicine Endocrinology",
    applicationSpecialty: "Endocrinology",
    clinicianTitle: "endocrinologist",
    category: "physician",
    blurb:
      "Answer curbside questions, deliver eConsults, and run tele-visits for diabetes, thyroid, adrenal, and pituitary cases from rural hospitals and clinics.",
    panelWork: [
      "Same-day curbside answers on diabetes, thyroid, and adrenal cases",
      "Written eConsults billable under CPT 99451 / 99452",
      "Direct patient tele-visits when the case warrants it",
      "Insulin start & titration guidance for primary care",
      "Thyroid nodule and abnormal-lab triage",
    ],
  },
  {
    slug: "cardiology",
    displayName: "Cardiology",
    seoName: "Telemedicine Cardiology",
    applicationSpecialty: "Cardiology",
    clinicianTitle: "cardiologist",
    category: "physician",
    blurb:
      "Tiered cardiology coverage for community and rural hospitals — echo/holter reads, curbsides, eConsults, and tele-visits so cases stay local.",
    panelWork: [
      "Abnormal EKG review and arrhythmia triage",
      "Chest pain risk stratification for primary care and ED",
      "Heart failure titration and hypertension optimization",
      "Pre-op cardiac clearance eConsults",
      "Tele-visits for valvular follow-up and syncope workup",
    ],
  },
  {
    slug: "rheumatology",
    displayName: "Rheumatology",
    seoName: "Telemedicine Rheumatology",
    applicationSpecialty: "Rheumatology",
    clinicianTitle: "rheumatologist",
    category: "physician",
    blurb:
      "Help rural providers manage RA, lupus, gout, and positive-ANA workups without a six-month referral wait. Curbside, eConsult, or tele-visit.",
    panelWork: [
      "Inflammatory arthritis diagnosis and biologic decisions",
      "Positive ANA / autoantibody triage",
      "PMR, gout, and vasculitis workups",
      "Steroid-taper and DMARD safety questions",
      "Tele-visits when a specialist face-to-face is needed",
    ],
  },
  {
    slug: "pulmonology",
    displayName: "Pulmonology / Critical Care",
    seoName: "Telemedicine Pulmonology",
    applicationSpecialty: "Pulmonology / Critical Care",
    clinicianTitle: "pulmonologist or intensivist",
    category: "physician",
    blurb:
      "COPD, asthma, incidental nodules, and ICU-level input for community hospitals. Pick the mix of curbside, eConsult, and tele-visit that fits your week.",
    panelWork: [
      "COPD and asthma optimization",
      "Pulmonary nodule triage and lung cancer screening follow-up",
      "Abnormal PFT interpretation",
      "Post-COVID and home-oxygen decisions",
      "ICU-level curbside support for community hospitals",
    ],
  },
  {
    slug: "nephrology",
    displayName: "Nephrology",
    seoName: "Telemedicine Nephrology",
    applicationSpecialty: "Nephrology",
    clinicianTitle: "nephrologist",
    category: "physician",
    blurb:
      "CKD, AKI, and electrolyte cases from rural hospitals and clinics — asynchronous eConsults or tele-visits, no on-call pager required.",
    panelWork: [
      "CKD staging and progression planning",
      "AKI follow-up and med-dosing questions",
      "Electrolyte and acid-base disturbances",
      "Resistant hypertension optimization",
      "Pre-dialysis planning and access decisions",
    ],
  },
  {
    slug: "neurology",
    displayName: "Neurology",
    seoName: "Telemedicine Neurology",
    applicationSpecialty: "Neurology",
    clinicianTitle: "neurologist",
    category: "physician",
    blurb:
      "Headache, seizure, TIA follow-up, neuropathy, and post-stroke care — plus optional acute tele-stroke shifts for board-certified vascular neurologists.",
    panelWork: [
      "Headache, seizure, and TIA follow-up eConsults",
      "Neuropathy and MS clarification questions",
      "Post-stroke and cognitive workups",
      "Optional acute tele-stroke coverage (vascular neuro)",
      "Tele-visits when the case warrants it",
    ],
    extraRequirements: [
      "Vascular / stroke fellowship preferred for tele-stroke shifts",
    ],
  },
  {
    slug: "hospitalist",
    displayName: "Hospitalist / Internal Medicine",
    seoName: "Telemedicine Hospitalist",
    applicationSpecialty: "Hospitalist / Internal Medicine",
    clinicianTitle: "hospitalist",
    category: "physician",
    blurb:
      "Rounding, admissions, cross-coverage, and nocturnist shifts for community and critical-access hospitals — dedicated teams, not locum roulette.",
    panelWork: [
      "Daytime rounding coverage",
      "24/7 nocturnist coverage",
      "Admissions, discharges, and cross-coverage",
      "Rapid response and code participation",
      "Swing-bed / observation management",
    ],
  },
  {
    slug: "infectious-disease",
    displayName: "Infectious Disease",
    seoName: "Telemedicine Infectious Disease",
    applicationSpecialty: "Infectious Disease",
    clinicianTitle: "infectious disease physician",
    category: "physician",
    blurb:
      "OPAT, complex antibiotic decisions, hardware infections, and inpatient ID curbsides for community hospitals and clinics.",
    panelWork: [
      "OPAT (outpatient parenteral antibiotic therapy) oversight",
      "Complex antibiotic selection and de-escalation",
      "Hardware / device infection triage",
      "Chronic wound and osteomyelitis input",
      "Inpatient ID curbsides and eConsults",
    ],
  },
  {
    slug: "dermatology",
    displayName: "Dermatology",
    seoName: "Telemedicine Dermatology",
    applicationSpecialty: "Dermatology",
    clinicianTitle: "dermatologist",
    category: "physician",
    blurb:
      "Fast, image-based curbsides for rashes, suspicious lesions, and post-biopsy planning. No visits to schedule — read the photo, send the plan.",
    panelWork: [
      "Rash and lesion image-based curbsides",
      "Suspicious lesion triage",
      "Acne, rosacea, psoriasis, eczema flare guidance",
      "Post-biopsy planning input",
      "Wound and nail question triage",
    ],
  },
  {
    slug: "case-management",
    displayName: "Case Management (RN)",
    seoName: "Telemedicine Case Manager",
    applicationSpecialty: "Case Management (RN)",
    clinicianTitle: "RN case manager",
    category: "allied",
    blurb:
      "Real-time observation vs. inpatient status support and MCG/InterQual-based documentation for ED and inpatient teams at community hospitals.",
    panelWork: [
      "Live observation vs. inpatient status determination",
      "MCG / InterQual guideline-based documentation",
      "ED case management support",
      "Concurrent utilization review handoff",
      "Denial-prevention documentation",
    ],
    extraRequirements: [
      "Active US RN license",
      "MCG or InterQual experience preferred",
      "3+ years case management experience (in place of the post-residency requirement)",
    ],
  },
  {
    slug: "respiratory-therapy",
    displayName: "Respiratory Therapy (RRT)",
    seoName: "Telemedicine Respiratory Therapist (Tele-RT)",
    applicationSpecialty: "Respiratory Therapy (RRT)",
    clinicianTitle: "registered respiratory therapist",
    category: "allied",
    blurb:
      "24/7 tele-RT coverage for community and critical-access hospitals — vent management, weaning, and complex respiratory support for the bedside team.",
    panelWork: [
      "Ventilator management and weaning guidance",
      "Complex respiratory patient assessment support",
      "BiPAP / CPAP set-up and troubleshooting",
      "Around-the-clock coverage for bedside nursing",
      "Rapid response respiratory input",
    ],
    extraRequirements: [
      "Active RRT credential",
      "3+ years post-credential experience",
      "Adult critical-care experience preferred",
    ],
  },
];

export const CAREER_SPECS_BY_SLUG: Record<string, CareerSpec> = Object.fromEntries(
  CAREER_SPECS.map((c) => [c.slug, c]),
);

/** Default postedAt date reused for JobPosting schema. */
export const DEFAULT_JOB_POSTED_AT = DEFAULT_POSTED;
